/* ═══════════════════════════════════════════
   BOOK DUPLICATE DETECTION & MERGING
   Fuzzy title matching + author comparison
═══════════════════════════════════════════ */

// Words to strip when comparing titles for duplicate detection
const NOISE_WORDS = [
  'special edition', "collector's edition", 'collectors edition', 'collector edition',
  'ebook bundle', 'e-book bundle', 'boxset', 'box set', 'omnibus',
  'deluxe edition', 'anniversary edition', 'illustrated edition',
  'paperback', 'hardcover', 'hardback', 'kindle edition', 'audiobook',
  'a novel', 'the novel', 'complete series', 'complete collection',
  'book 1', 'book 2', 'book 3', 'book 4', 'book 5', 'book 6',
  'book one', 'book two', 'book three', 'book four', 'book five',
  'volume 1', 'volume 2', 'volume 3', 'vol 1', 'vol 2', 'vol 3',
  '(1)', '(2)', '(3)', '(4)', '(5)',
  '#1', '#2', '#3', '#4', '#5',
];

/**
 * Normalize a title for comparison — strip noise words, punctuation, extra spaces
 */
export function normalizeTitle(title) {
  if (!title) return '';
  let t = title.toLowerCase().trim();
  // Remove parenthesized edition info
  t = t.replace(/\([^)]*edition[^)]*\)/gi, '');
  t = t.replace(/\([^)]*collection[^)]*\)/gi, '');
  t = t.replace(/\([^)]*bundle[^)]*\)/gi, '');
  // Remove noise words
  for (const word of NOISE_WORDS) {
    t = t.replace(new RegExp(word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '');
  }
  // Remove punctuation and extra spaces
  t = t.replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
  return t;
}

/**
 * Normalize an author name for comparison
 */
export function normalizeAuthor(author) {
  if (!author) return '';
  // Take first author if comma-separated list
  return author.split(',')[0].toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Calculate similarity between two strings (0 to 1)
 * Uses a simple token overlap approach — fast and good enough for titles
 */
export function titleSimilarity(a, b) {
  if (!a || !b) return 0;
  const tokensA = new Set(a.split(' ').filter(Boolean));
  const tokensB = new Set(b.split(' ').filter(Boolean));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;

  let overlap = 0;
  for (const t of tokensA) {
    if (tokensB.has(t)) overlap++;
  }

  // Jaccard-like: overlap / union
  const union = new Set([...tokensA, ...tokensB]).size;
  return overlap / union;
}

/**
 * Check if a book is a potential duplicate of any book in the existing list.
 * Returns the best match or null.
 * @param {Object} newBook - { title, author, isbn, googleBooksId }
 * @param {Array} existingBooks - array of book objects from the database
 * @param {Set} dismissedPairs - set of "id1|||id2" pairs that have been dismissed
 * @returns {{ match: Object, similarity: number } | null}
 */
export function findDuplicate(newBook, existingBooks, dismissedPairs = new Set()) {
  const normTitle = normalizeTitle(newBook.title);
  const normAuthor = normalizeAuthor(newBook.author);

  if (!normTitle) return null;

  let bestMatch = null;
  let bestSim = 0;

  for (const existing of existingBooks) {
    // Skip self
    if (existing.googleBooksId === newBook.googleBooksId) continue;
    if (existing.id === newBook.googleBooksId) continue;

    // Check dismissed pairs
    const pairKey1 = `${newBook.googleBooksId}|||${existing.id || existing.googleBooksId}`;
    const pairKey2 = `${existing.id || existing.googleBooksId}|||${newBook.googleBooksId}`;
    if (dismissedPairs.has(pairKey1) || dismissedPairs.has(pairKey2)) continue;

    // ISBN exact match is a strong signal
    if (newBook.isbn && existing.isbn && newBook.isbn === existing.isbn) {
      return { match: existing, similarity: 1.0 };
    }

    // Author must match (at least partially) for title comparison
    const existingAuthor = normalizeAuthor(existing.author);
    if (!existingAuthor || !normAuthor) continue;

    const authorSim = titleSimilarity(normAuthor, existingAuthor);
    if (authorSim < 0.5) continue; // Authors must share at least half their tokens

    // Compare normalized titles
    const existingTitle = normalizeTitle(existing.title);
    const sim = titleSimilarity(normTitle, existingTitle);

    if (sim > bestSim && sim >= 0.6) {
      bestSim = sim;
      bestMatch = existing;
    }
  }

  return bestMatch ? { match: bestMatch, similarity: bestSim } : null;
}

/**
 * Find all duplicate groups in a list of books.
 * Returns array of { books: [book1, book2, ...], baseTitle, author }
 */
export function findAllDuplicateGroups(books, dismissedPairs = new Set()) {
  const groups = [];
  const assigned = new Set();

  for (let i = 0; i < books.length; i++) {
    if (assigned.has(i)) continue;
    const book = books[i];
    const normTitle = normalizeTitle(book.title);
    const normAuthor = normalizeAuthor(book.author);
    if (!normTitle || !normAuthor) continue;

    const group = [book];
    assigned.add(i);

    for (let j = i + 1; j < books.length; j++) {
      if (assigned.has(j)) continue;
      const other = books[j];

      // Check dismissed pairs
      const pairKey1 = `${book.id}|||${other.id}`;
      const pairKey2 = `${other.id}|||${book.id}`;
      if (dismissedPairs.has(pairKey1) || dismissedPairs.has(pairKey2)) continue;

      const otherAuthor = normalizeAuthor(other.author);
      const authorSim = titleSimilarity(normAuthor, otherAuthor);
      if (authorSim < 0.5) continue;

      const otherTitle = normalizeTitle(other.title);
      const sim = titleSimilarity(normTitle, otherTitle);
      if (sim >= 0.6) {
        group.push(other);
        assigned.add(j);
      }
    }

    if (group.length > 1) {
      groups.push({
        books: group,
        baseTitle: book.title,
        author: book.author,
      });
    }
  }

  return groups;
}

/**
 * Merge two book records — combines tags, keeps better metadata.
 * @param {Object} primary - the book to keep (will be updated)
 * @param {Object} secondary - the book to merge in (will be deleted)
 * @param {string} preferCover - 'primary' or 'secondary'
 * @returns {Object} merged book data (without id)
 */
export function mergeBookRecords(primary, secondary, preferCover = 'primary') {
  // Combine admin tags — union of both
  const mergedAdminTags = { ...primary.adminTags };
  for (const [secKey, items] of Object.entries(secondary.adminTags || {})) {
    if (!mergedAdminTags[secKey]) mergedAdminTags[secKey] = {};
    for (const [item, val] of Object.entries(items)) {
      if (!mergedAdminTags[secKey][item]) {
        mergedAdminTags[secKey][item] = val;
      }
    }
  }

  // Combine auto tags — union of both
  const mergedAutoTags = { ...primary.autoTags };
  for (const [secKey, items] of Object.entries(secondary.autoTags || {})) {
    if (!mergedAutoTags[secKey]) mergedAutoTags[secKey] = {};
    for (const [item, val] of Object.entries(items)) {
      if (!mergedAutoTags[secKey][item]) {
        mergedAutoTags[secKey][item] = val;
      }
    }
  }

  // Combine resolved tags — union, prefer higher-authority source
  const mergedResolvedTags = { ...primary.resolvedTags };
  for (const [secKey, items] of Object.entries(secondary.resolvedTags || {})) {
    if (!mergedResolvedTags[secKey]) mergedResolvedTags[secKey] = {};
    for (const [item, info] of Object.entries(items)) {
      const existing = mergedResolvedTags[secKey][item];
      if (!existing) {
        mergedResolvedTags[secKey][item] = info;
      } else {
        // Prefer admin > community > unverified
        const priority = { admin: 3, community: 2, unverified: 1 };
        if ((priority[info.source] || 0) > (priority[existing.source] || 0)) {
          mergedResolvedTags[secKey][item] = info;
        }
      }
    }
  }

  const chosenCover = preferCover === 'secondary' ? (secondary.cover || primary.cover) : (primary.cover || secondary.cover);

  // Build merged edition note
  const existingNote = primary.mergedFrom || [];
  const newNote = [...existingNote, {
    title: secondary.title,
    googleBooksId: secondary.googleBooksId || secondary.id,
    mergedAt: new Date().toISOString(),
  }];

  return {
    title: primary.title,
    author: primary.author,
    googleBooksId: primary.googleBooksId || primary.id,
    isbn: primary.isbn || secondary.isbn || null,
    cover: chosenCover,
    description: primary.description || secondary.description || null,
    pageCount: primary.pageCount || secondary.pageCount || null,
    publishedDate: primary.publishedDate || secondary.publishedDate || null,
    publisher: primary.publisher || secondary.publisher || null,
    googleBooksLink: primary.googleBooksLink || secondary.googleBooksLink || null,
    adminTags: mergedAdminTags,
    autoTags: mergedAutoTags,
    resolvedTags: mergedResolvedTags,
    communityTags: primary.communityTags || secondary.communityTags || {},
    submissionCount: Math.max(primary.submissionCount || 0, secondary.submissionCount || 0),
    needsTagging: primary.needsTagging !== false && secondary.needsTagging !== false,
    dateAdded: primary.dateAdded || secondary.dateAdded,
    addedBy: primary.addedBy || secondary.addedBy,
    mergedFrom: newNote,
  };
}
